import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { getSalmoByNumero, getSalmosRelacionados, getTitulo, getTexto, getReflexao } from "@/lib/salmos";
import { useLang } from "@/lib/i18n";
import OrnamentoDourado from "@/components/OrnamentoDourado";
import BotaoPartilhar from "@/components/BotaoPartilhar";
import SalmoCard from "@/components/SalmoCard";
import AdsensePlaceholder from "@/components/AdsensePlaceholder";
import SEO from "@/components/SEO";

export default function SalmoPage() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const salmo = getSalmoByNumero(numero);

  if (!salmo) {
    return (
      <div className="px-6 py-32 text-center" data-testid="salmo-nao-encontrado">
        <div className="font-display text-3xl text-[var(--cor-texto)]">{t.salmo_nao_encontrado}</div>
        <button
          onClick={() => navigate("/salmos")}
          className="mt-8 font-display text-sm uppercase tracking-[0.22em] text-[var(--cor-dourado)] border border-[var(--cor-dourado)]/40 px-8 py-3 rounded-full hover:bg-[var(--cor-dourado)]/10 transition-all"
        >
          {t.ver_todos}
        </button>
      </div>
    );
  }

  const titulo = getTitulo(salmo, lang);
  const texto = getTexto(salmo, lang);
  const reflexao = getReflexao(salmo, lang);
  const primeiraLetra = texto.charAt(0);
  const restoTexto = texto.slice(1);
  const relacionados = getSalmosRelacionados(salmo.numero);

  return (
    <article className="relative" data-testid="salmo-page">
      <SEO
        title={`${t.salmo} ${salmo.numero} — ${titulo} | salmos.pt`}
        description={`${texto.split(/\s+/).slice(0, 20).join(" ")}...`}
      />

      <div className="mx-auto max-w-3xl px-6 pt-12">
        <nav className="flex items-center gap-2 font-display text-[10px] uppercase tracking-[0.22em] text-[var(--cor-texto-2)]" aria-label="Breadcrumb" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[var(--cor-dourado-claro)]">{t.inicio}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/salmos" className="hover:text-[var(--cor-dourado-claro)]">{t.salmos}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-[var(--cor-dourado)]">{t.salmo} {salmo.numero}</span>
        </nav>

        <AdsensePlaceholder position="topo" />

        <header className="mt-10 text-center">
          <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.salmo}</div>
          <div
            className="font-display text-9xl md:text-[12rem] text-[var(--cor-dourado)] font-light leading-none mt-2"
            data-testid="salmo-numero"
          >
            {salmo.numero}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[var(--cor-texto)] mt-6 font-light tracking-wide" data-testid="salmo-titulo">
            {titulo}
          </h1>
          <OrnamentoDourado className="mt-10" />
        </header>

        <div className="mt-16 font-serif text-[var(--cor-texto)] text-[20px] leading-[2]" data-testid="salmo-texto">
          <span className="float-left font-display text-7xl md:text-8xl text-[var(--cor-dourado)] leading-[0.85] mr-3 mt-1">
            {primeiraLetra}
          </span>
          {restoTexto}
        </div>

        <OrnamentoDourado simbolo="❧" className="mt-16" />

        {reflexao && (
          <div
            className="mt-12 rounded-sm border-l-2 border-[var(--cor-dourado)]/50 bg-[var(--cor-destaque)]/30 px-8 py-8"
            data-testid="salmo-reflexao"
          >
            <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.reflexao}</div>
            <p className="font-serif italic text-[var(--cor-texto)] text-lg leading-relaxed mt-4">{reflexao}</p>
          </div>
        )}

        <div className="mt-12 space-y-6">
          {salmo.tags && salmo.tags.length > 0 && (
            <div data-testid="salmo-tags">
              <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)] mb-3">{t.temas}</div>
              <div className="flex flex-wrap gap-2">
                {salmo.tags.map((tag) => (
                  <span key={tag} className="font-display text-xs uppercase tracking-[0.18em] text-[var(--cor-dourado)]/90 border border-[var(--cor-borda)] px-3 py-1.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
          {salmo.situacoes && salmo.situacoes.length > 0 && (
            <div data-testid="salmo-situacoes">
              <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)] mb-3">{t.ideal_para}</div>
              <div className="flex flex-wrap gap-2">
                {salmo.situacoes.map((s) => (
                  <span key={s} className="font-serif text-sm italic text-[var(--cor-texto-2)] border border-dashed border-[var(--cor-borda)] px-3 py-1.5 rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <OrnamentoDourado className="mt-16" />

        <div className="mt-12">
          <BotaoPartilhar salmo={salmo} />
        </div>

        <AdsensePlaceholder position="rodape" />
      </div>

      {relacionados.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 mt-24" data-testid="salmos-relacionados">
          <div className="text-center mb-12">
            <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.continua_leitura}</div>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--cor-texto)] mt-4 font-light">{t.salmos_relacionados}</h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {relacionados.map((s) => <SalmoCard key={s.numero} salmo={s} />)}
          </div>
        </section>
      )}
    </article>
  );
}
